/**
 * HistoryScreen — one question at a time.
 *
 * Sub-cards (all yes/no):
 *  0  usedMarijuana
 *  1  usedLsd
 *  2  usedOtherDrugs
 *  3  wasAddicted
 *  4  stableLovingChildhood
 *  5  carriesScars
 *  6  readyForLastingRelationship  (gate field — required)
 */
import { useState } from "react";
import { Button } from "../components/Button";
import { QuestionCard } from "../components/QuestionCard";
import { useFlow } from "../flow/FlowContext";
import { useT } from "../i18n";

const TOTAL = 7;

export function HistoryScreen() {
  const { profileAnswers, setProfileAnswers, next, canContinue } = useFlow();
  const t = useT();
  const h = profileAnswers.history;

  const patch = (partial: Partial<typeof h>) =>
    setProfileAnswers((p) => ({ ...p, history: { ...p.history, ...partial } }));

  const [card, setCard] = useState(0);
  const pos = t("questionnaire.progressLabel", { current: String(card + 1), total: String(TOTAL) });
  const goNext = () => setCard((i) => Math.min(i + 1, TOTAL - 1));
  const goBack = () => setCard((i) => Math.max(i - 1, 0));

  type HKey = keyof typeof h;
  const cards: { question: string; key: HKey }[] = [
    { question: t("history.marijuana"), key: "usedMarijuana" },
    { question: t("history.lsd"), key: "usedLsd" },
    { question: t("history.otherDrugs"), key: "usedOtherDrugs" },
    { question: t("history.addicted"), key: "wasAddicted" },
    { question: t("history.childhood"), key: "stableLovingChildhood" },
    { question: t("history.scars"), key: "carriesScars" },
    { question: t("history.lasting"), key: "readyForLastingRelationship" },
  ];

  const isLast = card === TOTAL - 1;
  const current = cards[card];
  const currentValue = h[current.key] as boolean | null;

  const handleYes = () => {
    patch({ [current.key]: true });
    if (!isLast) goNext();
  };
  const handleNo = () => {
    patch({ [current.key]: false });
    if (!isLast) goNext();
  };

  return (
    <div className="screen-enter" style={{ flex: 1 }}>
      <QuestionCard question={current.question} position={pos}>
        <div className="q-options">
          <button
            type="button"
            className={`q-option${currentValue === true ? " is-selected" : ""}`}
            onClick={handleYes}
            aria-pressed={currentValue === true}
          >
            <strong>{t("common.yes")}</strong>
          </button>
          <button
            type="button"
            className={`q-option${currentValue === false ? " is-selected" : ""}`}
            onClick={handleNo}
            aria-pressed={currentValue === false}
          >
            <strong>{t("common.no")}</strong>
          </button>
        </div>
        {card > 0 && (
          <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
            ← {t("shell.back")}
          </button>
        )}
      </QuestionCard>
      {isLast && (
        <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Button onClick={next} disabled={!canContinue}>
            {t("common.continue")}
          </Button>
        </div>
      )}
    </div>
  );
}
